import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.getSubscriptionStatus();
      if (response && typeof response === 'object') {
        setSubscriptionStatus(response);
      }
    } catch (err) {
      console.error('Error loading subscription status:', err);
      setError(err.message || 'Failed to load subscription status');
      // Set default status if API fails
      setSubscriptionStatus({
        hasAccess: false,
        inTrial: false,
        trialExpired: false,
        remainingTrialDays: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadSubscriptionStatus();
    
    // Refresh subscription status every 5 minutes
    const interval = setInterval(loadSubscriptionStatus, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [loadSubscriptionStatus]);

  // Refresh subscription status after subscription changes
  useEffect(() => {
    const handleSubscriptionUpdated = () => {
      loadSubscriptionStatus();
    };

    window.addEventListener('subscription-updated', handleSubscriptionUpdated);
    return () => window.removeEventListener('subscription-updated', handleSubscriptionUpdated);
  }, [loadSubscriptionStatus]);

  const plan = subscriptionStatus?.subscriptionPlan ?? 'FREE';
  const isPremium =
    (subscriptionStatus?.premium === true) ||
    (subscriptionStatus?.hasAccess && (plan === 'PREMIUM' || plan === 'LIFETIME'));

  const value = {
    subscriptionStatus,
    loading,
    error,
    hasAccess: subscriptionStatus?.hasAccess ?? false,
    inTrial: subscriptionStatus?.inTrial ?? false,
    trialExpired: subscriptionStatus?.trialExpired ?? false,
    remainingTrialDays: subscriptionStatus?.remainingTrialDays ?? 0,
    plan,
    isPremium: !!isPremium,
    refreshSubscriptionStatus: loadSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};









